import React, { useEffect } from 'react';
import {
  PaginationState,
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnFiltersState,
  SortingState,
  ColumnDef
} from '@tanstack/react-table';
import { TestExecutionData, TestExecutionStatus } from './const/TestExecution';
import { testExecutionService } from '../../../api/testExecutionService';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Pagination, Button, Tag, Select, MenuItem } from '@dsi/react-eds';
import { useTheme } from '../../../hooks/useTheme';
import { useLoader } from '../../../hooks/useLoader';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import styles from './TestExecutionTable.module.css';
import keycloak from '../../../services/keycloak';

interface TestExecutionTableProps {
  onTestExecutionIdClick?: (id: string) => void;
  onApply: (data: {
    testCollectionId: string;
    testCollectionName: string;
    workflowId: string;
    workflowDbId: string;
    autoFlowDbId: string;
    testServiceId: string;
    rAppId: string;
    productLine: string;
    username: string;
  }) => void;
  status?: string;
}

const TestExecutionTable: React.FC<TestExecutionTableProps> = ({ onTestExecutionIdClick, onApply, status }) => {
  const { theme } = useTheme();
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();

  const [data, setData] = React.useState<TestExecutionData[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [debouncedFilters, setDebouncedFilters] = React.useState<ColumnFiltersState>([]);
  const [filterTimeout, setFilterTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [showFilters, setShowFilters] = React.useState(false);

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  });

  const testExecutionColumns = React.useMemo<ColumnDef<TestExecutionData>[]>(
    () => [
      {
        accessorKey: 'test_execution_id',
        header: 'Test Execution ID',
        cell: (info) => {
          const id = info.getValue() as string;
          const status = info.row.original.status;

          const isRunning = status.toLowerCase() === TestExecutionStatus.RUNNING.toLowerCase();

          if (isRunning) {
            console.log('is running status', status);

            return (
              <span
                style={{
                  color: '#999',
                  cursor: 'not-allowed',
                  textDecoration: 'none'
                }}
                title="Execution is running, cannot view details"
              >
                {id}
              </span>
            );
          }

          return (
            <a
              href={`#/test-execution/${id}`}
              aria-label={`Go to Test Summaryn ${id}`}
              title={`Go to Test Summary ${id}`}
              onClick={(e) => {
                e.preventDefault();
                if (onTestExecutionIdClick) {
                  onTestExecutionIdClick(id);
                }
              }}
            >
              {id}
            </a>
          );
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'workflow_ticket_id',
        header: 'Workflow ID',
        cell: (info) => (
          <a
            href={`#/workflow/${info.getValue()}`}
            target="_blank"
            aria-label={`Go to pipeline ${info.getValue()}`}
            title={`Go to pipeline ${info.getValue()}`}
            onClick={(e) => {
              e.preventDefault();
              window.open(`/workflow/${info.getValue()}`, '_blank');
            }}
          >
            {info.getValue() as string}
          </a>
        ),
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'workflow_name',
        header: 'Workflow Name',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'test_collection_name',
        header: 'Test Collection',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'product_version',
        header: 'Product Version',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'product_number',
        header: 'Product Number',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'createdAt',
        header: 'Start Timestamp',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'duration',
        header: 'Duration',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'trigger_type',
        header: 'Trigger Type',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'status',
        header: 'Results',
        cell: (info) => {
          const status = info.getValue() as TestExecutionStatus;
          switch (status) {
            case TestExecutionStatus.SUCCESS:
              return (
                <ReactEDSWrapper component={Tag} big color="green" icon="success">
                  {status}
                </ReactEDSWrapper>
              );
            case TestExecutionStatus.FAILURE:
              return (
                <ReactEDSWrapper component={Tag} big color="red" icon="failed">
                  {status}
                </ReactEDSWrapper>
              );
            case TestExecutionStatus.WARNING:
              return (
                <ReactEDSWrapper component={Tag} big color="orange" icon="triangle-warning">
                  {status}
                </ReactEDSWrapper>
              );
            case TestExecutionStatus.RUNNING:
              return (
                <ReactEDSWrapper component={Tag} big color="blue" icon="time">
                  {status}
                </ReactEDSWrapper>
              );
            default:
              return <ReactEDSWrapper component={Tag}>{status}</ReactEDSWrapper>;
          }
        },
        footer: (props) => props.column.id
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className={styles.actionButtons}>
            <ReactEDSWrapper
              component={Button}
              icon="block"
              aria-label={`Stop`}
              title="Stop"
              disabled={row.original.status !== TestExecutionStatus.RUNNING}
              onClick={() => console.log('Stop', row.original.test_execution_id)}
            />
            <ReactEDSWrapper
              component={Button}
              icon="reload"
              aria-label={`Re-try`}
              title="Re-try"
              disabled={row.original.status === TestExecutionStatus.RUNNING}
              onClick={() => {
                const execution = row.original;
                const submitData = {
                  testCollectionId: execution?.test_collection_id || '',
                  testCollectionName: execution.test_collection_name,
                  workflowId: execution.workflow_ticket_id,
                  workflowDbId: execution?.workflow_id || '',
                  autoFlowDbId: execution?.automation_flow_id || '',
                  testServiceId: execution?.test_service_id || '',
                  rAppId: execution?.rapp_id || '',
                  productLine: execution?.product_line || '',
                  username: keycloak.tokenParsed?.preferred_username || ''
                };
                onApply(submitData);
              }}
            />
          </div>
        ),
        enableSorting: false,
        enableColumnFilter: false
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onTestExecutionIdClick]
  );

  const getColumnMinWidth = (columnId: string) => {
    const widths = {
      test_execution_id: '180px',
      workflow_ticket_id: '200px',
      workflow_name: '250px',
      test_collection_name: '250px',
      product_version: '250px',
      product_number: '250px',
      createdAt: '230px',
      duration: '160px',
      trigger_type: '130px',
      status: '100px',
      actions: '100px'
    };
    return widths[columnId as keyof typeof widths] || '120px';
  };

  const table = useReactTable({
    data,
    columns: testExecutionColumns,
    pageCount: Math.ceil(total / pagination.pageSize),
    state: {
      pagination,
      columnFilters,
      sorting
    },
    onPaginationChange: setPagination,
    onColumnFiltersChange: (updater) => {
      const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
      setColumnFilters(newFilters);
      if (filterTimeout) clearTimeout(filterTimeout);
      const timeout = setTimeout(() => {
        setDebouncedFilters(newFilters);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }, 500);
      setFilterTimeout(timeout);
    },
    onSortingChange: (updater) => {
      setSorting(updater);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    enableMultiSort: false,
    enableSortingRemoval: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    debugTable: true
  });

  useEffect(() => {
    if (status) {
      const statusFilter = [{ id: 'status', value: status }];
      setColumnFilters(statusFilter);
      if (filterTimeout) clearTimeout(filterTimeout);
      const timeout = setTimeout(() => {
        setDebouncedFilters(statusFilter);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }, 500);
      setFilterTimeout(timeout);
    }
  }, [status]);

  useEffect(() => {
    const fetchTestExecutions = async () => {
      showLoader();
      try {
        const sortField = sorting[0]?.id || 'created_at';
        const sortDirection = sorting[0]?.desc ? 'DESC' : 'ASC';
        const filters = debouncedFilters.reduce((acc, filter) => {
          if (filter.id === 'status' && Array.isArray(filter.value)) {
            acc[filter.id] = filter.value.join(',');
          } else {
            acc[filter.id] = filter.value;
          }
          return acc;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }, {} as any);
        console.log('API Request params:', {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          filters,
          sortBy: sortField,
          sortOrder: sortDirection
        });

        const response = await testExecutionService.getTestExecutions(
          pagination.pageIndex + 1,
          pagination.pageSize,
          filters,
          sortField,
          sortDirection
        );

        setData(response.data);
        setTotal(response.total);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.error('Failed to fetch test executions:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch test executions. Please try again.';
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description: errorMessage,
            timestamp: new Date(),
            color: 'red'
          }
        ]);
      } finally {
        setTimeout(() => {
          hideLoader();
        }, 500);
      }
    };

    fetchTestExecutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sorting, debouncedFilters]);

  return (
    <div className={styles.container}>
      {total === 0 && (
        <div className={styles.noDataContainer}>
          <i className={`icon icon-info ${styles.dangerIcon}`} />
          <div className={styles.noDataTitle}>No Test Executions Found</div>
        </div>
      )}
      {total > 0 && (
        <>
          <div className={styles.header}>
            <span className={styles.itemCount}>
              {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
              {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, total)} of {total} items
            </span>
            <div className={styles.buttonGroup}>
              <ReactEDSWrapper component={Button} icon={showFilters ? 'filter-off' : 'filter'} onClick={() => setShowFilters(!showFilters)}>
                {showFilters ? 'Close Filters' : ' Filters'}
              </ReactEDSWrapper>
              {showFilters && (
                <ReactEDSWrapper
                  component={Button}
                  icon="filter-clear"
                  onClick={() => {
                    setColumnFilters([]);
                    setDebouncedFilters([]);
                    setPagination({ pageIndex: 0, pageSize: 10 });
                  }}
                >
                  Clear Filters
                </ReactEDSWrapper>
              )}
            </div>
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className={styles.headerRow}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={styles.headerCell}
                        style={{
                          textAlign: 'left',
                          padding: '12px 16px',
                          minWidth: getColumnMinWidth(header.column.id)
                        }}
                      >
                        <div
                          className={header.column.getCanSort() ? styles.sortableHeader : styles.unsortableHeader}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className={styles.sortIcon}>
                              {header.column.getIsSorted() === 'asc' ? (
                                <i className="icon icon-sort-up" style={{ transform: 'rotate(180deg)' }}></i>
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <i className="icon icon-sort-down"></i>
                              ) : (
                                <i className="icon icon-sorting"></i>
                              )}
                            </span>
                          )}
                        </div>
                        {header.column.getCanFilter() && showFilters ? (
                          <div className={styles.filterContainer}>
                            {header.column.id === 'status' ? (
                              <ReactEDSWrapper
                                component={Select}
                                type="multi"
                                label="Select options"
                                style={{ fontWeight: 'normal' }}
                                value={header.column.getFilterValue() || []}
                                onChange={(event: React.FormEvent<HTMLElement>) => {
                                  header.column.setFilterValue(event);
                                }}
                              >
                                <ReactEDSWrapper component={MenuItem} value={TestExecutionStatus.SUCCESS}>
                                  Success
                                </ReactEDSWrapper>
                                <ReactEDSWrapper component={MenuItem} value={TestExecutionStatus.FAILURE}>
                                  Failure
                                </ReactEDSWrapper>
                                <ReactEDSWrapper component={MenuItem} value={TestExecutionStatus.WARNING}>
                                  Warning
                                </ReactEDSWrapper>
                                <ReactEDSWrapper component={MenuItem} value={TestExecutionStatus.RUNNING}>
                                  Running
                                </ReactEDSWrapper>
                              </ReactEDSWrapper>
                            ) : (
                              <input
                                type="text"
                                value={(header.column.getFilterValue() ?? '') as string}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                placeholder={`Filter`}
                                className={styles.filterInput}
                              />
                            )}
                          </div>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={styles.tableRow}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '';
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={styles.tableCell}
                        style={{
                          borderTop: `1px solid ${!theme ? '#404040' : '#242424'}`
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ReactEDSWrapper
            component={Pagination}
            numPages={table.getPageCount()}
            selectedPage={table.getState().pagination.pageIndex + 1}
            onPaginationChangePage={(page: number) => {
              table.setPageIndex(page - 1);
            }}
            optionsList={[
              { value: '10', label: '10', active: table.getState().pagination.pageSize === 10 },
              { value: '20', label: '20', active: table.getState().pagination.pageSize === 20 },
              { value: '30', label: '30', active: table.getState().pagination.pageSize === 30 },
              { value: '40', label: '40', active: table.getState().pagination.pageSize === 40 },
              { value: '50', label: '50', active: table.getState().pagination.pageSize === 50 }
            ]}
            onPaginationChangeSelect={(size: string) => {
              table.setPageSize(Number(size));
              table.setPageIndex(0);
            }}
            withActions
          />
        </>
      )}
    </div>
  );
};

export default TestExecutionTable;
